import type {
  MetricGetPayload,
  MetricGroupGetPayload,
  MetricGroupSelect,
  MetricSelect,
  MetricSyncAuditGetPayload,
  MetricSyncAuditSelect,
  MetricValueGetPayload,
  MetricValueSelect,
  UnitGetPayload,
  UnitSelect,
  UserMetricGetPayload,
  UserMetricGroupSelect,
  UserMetricSelect,
} from '@erikdakoda/database/types';
import { UserForIdentificationSelect } from '@erikdakoda/auth/types';

/** Minimal metric fields for find/list AI tools. */
export const MetricForAiToolsSearchSelect = {
  id: true,
  slug: true,
  name: true,
  shortName: true,
  category: true,
  description: true,
  calculation: true,
  betterValues: true,
} satisfies MetricSelect;

/** Default read mask for Metric AI tools. */
export const MetricForEditSelect = {
  id: true,
  slug: true,
  name: true,
  shortName: true,
  description: true,
  category: true,
  measure: true,
  unitId: true,
  unitUsId: true,
  chartType: true,
  decimals: true,
  minValue: true,
  maxValue: true,
  valueStep: true,
  betterValues: true,
  goalLow: true,
  goalMid: true,
  baseMetricId: true,
  dependencies: true,
  calculation: true,
  source: true,
  scaleLabels: true,
  dashboardTimePeriod: true,
  private: true,
  publishedAt: true,
} satisfies MetricSelect;

/** UserMetric fields returned by add/update metric AI tools. */
export const UserMetricForEditSelect = {
  id: true,
  metricId: true,
  unitId: true,
  decimals: true,
  minValue: true,
  maxValue: true,
  valueStep: true,
  betterValues: true,
  goalLow: true,
  goalMid: true,
  baseline: true,
  targetValue: true,
  targetDate: true,
  startDate: true,
  baseRule: true,
  syncedAt: true,
  calculationError: true,
} satisfies UserMetricSelect;

/** Minimal UserMetric fields for find/list AI tools. */
export const UserMetricForAiToolsSearchSelect = {
  id: true,
  metricId: true,
  unitId: true,
  goalLow: true,
  goalMid: true,
  baseline: true,
  targetValue: true,
} satisfies UserMetricSelect;

/** Default read mask for MetricGroup AI tools. */
export const MetricGroupForEditSelect = {
  id: true,
  slug: true,
  name: true,
  description: true,
  icon: true,
  metricIds: true,
  primaryMetricId: true,
  staleDays: true,
  private: true,
  publishedAt: true,
} satisfies MetricGroupSelect;

/** Minimal MetricGroup fields for find/list AI tools. */
export const MetricGroupForAiToolsSearchSelect = {
  id: true,
  slug: true,
  name: true,
  description: true,
  icon: true,
  primaryMetricId: true,
} satisfies MetricGroupSelect;

/** Default read mask for UserMetricGroup AI tools. */
export const UserMetricGroupForEditSelect = {
  id: true,
  metricGroupId: true,
  ownerGroupKey: true,
} satisfies UserMetricGroupSelect;

/** Minimal UserMetricGroup fields for find/list AI tools. */
export const UserMetricGroupForAiToolsSearchSelect = {
  id: true,
  metricGroupId: true,
  ownerGroupKey: true,
} satisfies UserMetricGroupSelect;

/** Default read mask for Unit AI tools. */
export const UnitForEditSelect = {
  id: true,
  slug: true,
  name: true,
  measure: true,
  plural: true,
  abbrev: true,
  suffix: true,
  system: true,
  toAnchor: true,
  toAnchorOffset: true,
  private: true,
  publishedAt: true,
} satisfies UnitSelect;

/** Minimal Unit fields for find/list AI tools. */
export const UnitForAiToolsSearchSelect = {
  id: true,
  slug: true,
  name: true,
  abbrev: true,
  measure: true,
  system: true,
} satisfies UnitSelect;

const UnitSnippetSelect = {
  id: true,
  slug: true,
  name: true,
  abbrev: true,
} as const;

/** Unit fields required for unitShapeFromDb and conversion/formatting. */
export const UnitForConversionSelect = {
  measure: true,
  slug: true,
  name: true,
  plural: true,
  abbrev: true,
  suffix: true,
  prefix: true,
  shortSlug: true,
  system: true,
  toAnchor: true,
  toAnchorOffset: true,
  isAnchor: true,
  anchorRatio: true,
} satisfies UnitSelect;

const MetricSnippetSelect = {
  id: true,
  slug: true,
  name: true,
} as const;

/** Unit fields for admin DataGrid. */
export const UnitForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  ownerId: true,
  owner: { select: UserForIdentificationSelect },
  measure: true,
  slug: true,
  name: true,
  plural: true,
  abbrev: true,
  suffix: true,
  prefix: true,
  shortSlug: true,
  system: true,
  toAnchor: true,
  toAnchorOffset: true,
  isAnchor: true,
  anchorRatio: true,
  private: true,
  publishedAt: true,
} satisfies UnitSelect;

export type UnitForGrid = UnitGetPayload<{
  select: typeof UnitForGridSelect;
}>;

/** Metric Library fields for admin DataGrid. */
export const MetricForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  ownerId: true,
  owner: { select: UserForIdentificationSelect },
  name: true,
  slug: true,
  shortName: true,
  description: true,
  category: true,
  measure: true,
  unitId: true,
  unit: { select: UnitSnippetSelect },
  unitUsId: true,
  unitUs: { select: UnitSnippetSelect },
  chartType: true,
  decimals: true,
  minValue: true,
  maxValue: true,
  valueStep: true,
  betterValues: true,
  goalLow: true,
  goalMid: true,
  baseRule: true,
  baseMetricId: true,
  dependencies: true,
  calculation: true,
  source: true,
  hiddenFromLibrary: true,
  scaleLabels: true,
  sourceInfo: true,
  icon: true,
  dashboardTimePeriod: true,
  private: true,
  publishedAt: true,
} satisfies MetricSelect;

export type MetricForGrid = MetricGetPayload<{
  select: typeof MetricForGridSelect;
}>;

/** MetricGroup fields for admin DataGrid. */
export const MetricGroupForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  ownerId: true,
  owner: { select: UserForIdentificationSelect },
  name: true,
  slug: true,
  description: true,
  icon: true,
  metricIds: true,
  primaryMetricId: true,
  staleDays: true,
  private: true,
  publishedAt: true,
} satisfies MetricGroupSelect;

export type MetricGroupForGrid = MetricGroupGetPayload<{
  select: typeof MetricGroupForGridSelect;
}>;

/** UserMetric fields for admin DataGrid. */
export const UserMetricForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  ownerId: true,
  owner: { select: UserForIdentificationSelect },
  metricId: true,
  metric: { select: MetricSnippetSelect },
  unitId: true,
  unit: { select: UnitSnippetSelect },
  ownerMetricKey: true,
  decimals: true,
  minValue: true,
  maxValue: true,
  valueStep: true,
  betterValues: true,
  goalLow: true,
  goalMid: true,
  baseline: true,
  targetValue: true,
  targetDate: true,
  startDate: true,
  baseRule: true,
  syncedAt: true,
  calculationError: true,
} satisfies UserMetricSelect;

export type UserMetricForGrid = UserMetricGetPayload<{
  select: typeof UserMetricForGridSelect;
}>;

/** MetricValue fields for admin DataGrid. */
export const MetricValueForGridSelect = {
  id: true,
  createdAt: true,
  updatedAt: true,
  userMetricId: true,
  userMetric: {
    select: {
      id: true,
      ownerId: true,
      owner: { select: UserForIdentificationSelect },
      metricId: true,
      metric: { select: MetricSnippetSelect },
    },
  },
  measuredAt: true,
  localDay: true,
  value: true,
  source: true,
  sourceId: true,
  origin: true,
  originName: true,
} satisfies MetricValueSelect;

export type MetricValueForGrid = MetricValueGetPayload<{
  select: typeof MetricValueForGridSelect;
}>;

/** MetricSyncAudit fields for admin DataGrid (view + delete only). */
export const MetricSyncAuditForGridSelect = {
  id: true,
  createdAt: true,
  userMetricId: true,
  userMetric: {
    select: {
      id: true,
      ownerId: true,
      owner: { select: UserForIdentificationSelect },
      metricId: true,
      metric: { select: MetricSnippetSelect },
    },
  },
  source: true,
  created: true,
  updated: true,
  recomputed: true,
  error: true,
  syncedAt: true,
} satisfies MetricSyncAuditSelect;

export type MetricSyncAuditForGrid = MetricSyncAuditGetPayload<{
  select: typeof MetricSyncAuditForGridSelect;
}>;

/** `Label.listId` for the user's added-groups ordering list (one Label, `ItemLabel` per group). */
export const METRIC_GROUPS_LABEL_LIST_ID = 'metricGroups';

/** `Label.listId` for the user's metric sidebar folders. */
export const METRIC_FOLDERS_LABEL_LIST_ID = 'metrics';
